import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import { useIsAuthenticated, useUser } from "../authentication/authentication";
import AdminNav from "../components/navs/private/AdminNav";
import AdminSideNav from "../components/navs/private/AdminSideNav";
import PhoneAdminSideNav from "../components/navs/private/PhoneAdminSideNav";
import { useSelector } from "react-redux";
import Footer from "../components/private/dashboard/Footer";
import { useHotkeys } from "react-hotkeys-hook";
import { useAutoClearNavigation } from "../hooks/useAutoClearNavigation";
import { connectSocket } from "../socket";
import { receiveDataAction } from "../redux/realTimeData/realTimeDataActions";
import { useDispatch } from "react-redux";
import AiSidePanel from "../private/ai/components/AiSidePanel";
import { logout } from "../redux/auth/authActions";
import api from "../api";
import errorChecker from "../utils/errorChecker";
import { showEmergencyAlert, hideEmergencyAlert } from "../redux/emergencyAlert/emergencyAlertActions";
import { arClass } from "../utils/useArClass";
import GeoErrorPage from "../public/GeoErrorPage";
import EmergencyAlert from "../components/EmergencyAlert";

export default function PrivateRouteController() {
  const [isSideNavVisible, setSideNavVisible] = useState(false);
  const darkMode = useSelector((state) => state.settings.darkMode);
  const isAuthenticated = useIsAuthenticated();
  const errorState = useSelector((state) => state?.error) || null;
  const isGeoBlocked = errorState?.errType === "geoBlock";
  const dispatch = useDispatch();
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage,
  );
 const dropdownLinks = {
  Patients: {
          mainName: "Patients",
          items: [
            {
              name: "View All Patients",
              url: "/patients",
              method: "get",
            },
            {
              name: "Add Patient",
              url: "/create-patient",
              method: "post",
            },
          ],
          permission: {
            permissionKey: "patientsPermissions",
            methods: ["get", "post"],
          },
        },
  
        Appointments: {
          mainName: "Appointments",
          items: [
            {
              name: "View All Appointments",
              url: "/appointments",
              method: "get",
            },
            {
              name: "Add Appointment",
              url: "/create-appointment",
              method: "post",
            },
          ],
          permission: {
            permissionKey: "appointmentsPermissions",
            methods: ["get", "post"],
          },
        },
  
        Visits: {
          mainName: "Visits",
          items: [
            {
              name: "View All Visits",
              url: "/visits",
              method: "get",
            },
            {
              name: "Add Visit",
              url: "/create-visit",
              method: "post",
            },
          ],
          permission: {
            permissionKey: "visitsPermissions",
            methods: ["get", "post"],
          },
        },
  
        Payments: {
          mainName: "Payments",
          items: [
            {
              name: "View All Payments",
              url: "/payments",
              method: "get",
            },
            {
              name: "Add Payment",
              url: "/create-payment",
              method: "post",
            },
          ],
          permission: {
            permissionKey: "paymentsPermissions",
            methods: ["get", "post"],
          },
        },
}

const singleLinks = [
  {
          name: "My Profile",
          url: "/profile",
  
        },
        {
          name: "Medications",
          url: "/medicaments",
        
        },
        {
          name: "Settings",
          url: "/settings",
      
        },
]

  // Clear navigation state on route change
  useAutoClearNavigation();
  // Initialize the navigation hook
  const navigate = useNavigate();

  // Create a combined links structure from dropdownLinks and singleLinks
  const links = {
    ...dropdownLinks,
    single: { items: singleLinks }
  };

  // Safely resolve a link by section and item index
  const safeNavigateLink = (sectionIdx, itemIdx) => {
    try {
      const keys = Object.keys(links);
      const sectionKey = keys[sectionIdx];
      const section = sectionKey ? links[sectionKey] : undefined;
      const items = section?.items || [];
      const item = items[itemIdx];
      if (url) navigate(url);
    } catch (e) {
      // fail silently — hotkey pressed but structure not ready
    }
  };

  // Handle global keyboard shortcuts for navigation (safe guards)
  useHotkeys("ctrl+alt+p", () => safeNavigateLink(0, 0)); // All Patients
  useHotkeys("ctrl+alt+u", () => safeNavigateLink(0, 1)); // Add Patient

  useHotkeys("ctrl+alt+c", () => safeNavigateLink(1, 0)); // All Consultations
  useHotkeys("ctrl+alt+v", () => safeNavigateLink(1, 1)); // Add Consultation

  useHotkeys("ctrl+alt+x", () => safeNavigateLink(2, 0)); // All Controls
  useHotkeys("ctrl+alt+y", () => safeNavigateLink(2, 1)); // Add Control

  useHotkeys("ctrl+alt+a", () => safeNavigateLink(3, 0)); // All Appointments
  useHotkeys("ctrl+alt+s", () => safeNavigateLink(3, 1)); // Add Appointment

  useHotkeys("ctrl+alt+t", () => safeNavigateLink(4, 0)); // All Payments
  useHotkeys("ctrl+alt+z", () => safeNavigateLink(4, 1)); // Add Payment

  // Global logout hotkey — show confirmation dialog before logging out
  useHotkeys("ctrl+alt+q", () => {
    const confirmTexts = {
      en: {
        title: "Confirm logout",
        message: "Are you sure you want to log out?",
      },
      fr: {
        title: "Confirmer la déconnexion",
        message: "Êtes-vous sûr de vouloir vous déconnecter ?",
      },
      ar: {
        title: "تأكيد تسجيل الخروج",
        message: "هل أنت متأكد أنك تريد تسجيل الخروج؟",
      },
    };
    const t = confirmTexts[defaultLanguage] || confirmTexts.en;

    dispatch(
      showAlert({
        type: "confirmation",
        title: t.title,
        message: t.message,
        onCancel: () => dispatch(clearConfirmationAlerts()),
        onConfirm: () => {
          api
            .post("auth/logout/")
            .then(() => {
              localStorage.clear();
              dispatch(logout());
              dispatch(clearConfirmationAlerts());
              navigate("/login");
            })
            .catch((e) => {
              errorChecker(e, dispatch);
            });
        },
      }),
    );
  }); // Logout

  // If the user is geo-blocked, show the GeoErrorPage after all hooks have been initialized

  const toggleSideNavVisibility = () => {
    setSideNavVisible(!isSideNavVisible);
  };

  if (!isAuthenticated) {
    // If not authenticated, redirect to login page
    return <Navigate to="/login" replace />;
  }
  const user = useUser();
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;

    const socket = connectSocket();

    const handleReceiveData = (payload) => {
      
      const from = String(payload?.from || ""); 
      if(from && from === userId) return; 
      dispatch(receiveDataAction(payload));
     
    };

    const handleEmergencyAlert = (alertData) => {
      
      dispatch(showEmergencyAlert({
        id: alertData.id || `emergency-${Date.now()}`,
        type: alertData.type || 'high',
        alertType: alertData.alertType || 'Security Alert',
        data: alertData,
      }));
    };
    socket.on("receiveData", handleReceiveData);
    socket.on("emergencyAlert", handleEmergencyAlert);

    const handleBeforeUnload = () => {
      if (socket && socket.connected) {
        socket.disconnect(); // notify server immediately
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      try {
        socket.off("receiveData", handleReceiveData);
        socket.off("emergencyAlert", handleEmergencyAlert);
        window.removeEventListener("beforeunload", handleBeforeUnload);

        // Important: disconnect only if this effect owns the socket
        if (socket && socket.connected) {
          socket.disconnect();
        }
      } catch (e) {}
    };
  }, [userId, dispatch]);

  const className =
    defaultLanguage === "ar"
      ? `[direction:rtl] [text-align:right] pr-64 max-xl:pr-24 max-sm:pr-0 ${
          darkMode ? "dashboard-color-dark" : "dashboard-color"
        }`
      : `pl-64 max-xl:pl-24 max-sm:pl-0 ${
          darkMode ? "dashboard-color-dark" : "dashboard-color"
        }`;
  if (isGeoBlocked) {
    return <GeoErrorPage />;
  }

  return (
    <div className={arClass("dashboard")}>
      <AiSidePanel />
      <EmergencyAlert />
      <AdminSideNav />
      {isSideNavVisible && (
        <PhoneAdminSideNav
          isSideNavVisible={isSideNavVisible}
          toggleSideNavVisibility={toggleSideNavVisibility}
        />
      )}
      <div className={className}>
        
        <AdminNav
          isSideNavVisible={isSideNavVisible}
          toggleSideNavVisibility={toggleSideNavVisibility}
        />
        <main className={arClass("min-h-vh pt-20 max-sm:mx-0")}>
          <Outlet />
          <Footer />
        </main>
      </div>
    </div>
  );
}
